import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModal } from '../context/ModalDialogContext';
import ErrorBoundary from './ErrorBoundary';
import { isDarkColor, resolveSolidColor } from '../utils/themeUtils';

export const METAL_STYLES = {
  white: { bg: '#ffffff', border: '#e2e8f0', text: '#334155', label: 'White' },
  gold: { bg: 'linear-gradient(135deg, #fef08a 0%, #d4af37 50%, #854d0e 100%)', border: '#ca8a04', text: '#78350f', label: 'Gold' },
  silver: { bg: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 50%, #64748b 100%)', border: '#94a3b8', text: '#334155', label: 'Silver' },
  copper: { bg: 'linear-gradient(135deg, #ffedd5 0%, #b45309 50%, #7c2d12 100%)', border: '#c2410c', text: '#7c2d12', label: 'Copper' },
  bronze: { bg: 'linear-gradient(135deg, #fef3c7 0%, #92400e 50%, #451a03 100%)', border: '#78350f', text: '#451a03', label: 'Bronze' },
  obsidian: { bg: 'linear-gradient(135deg, #334155 0%, #0f172a 50%, #020617 100%)', border: '#d4af37', text: '#fde047', label: 'Obsidian' }
};

export const safeParseConfig = (config) => {
  if (!config) return {};
  if (typeof config === 'object') return config;
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

export const getSafeMetalInfo = (metalKey) => {
  const key = typeof metalKey === 'string' ? metalKey.trim().toLowerCase() : 'gold';
  return METAL_STYLES[key] || METAL_STYLES.gold || {
    bg: '#334155',
    border: '#ca8a04',
    text: '#78350f',
    label: 'Gold'
  };
};

export function FaceTemplateCard({
  template,
  isSelected,
  gridZoom,
  onSelect,
  onDoubleClick
}) {
  const t = template || {};
  const cfg = safeParseConfig(t.config);
  const side = cfg.side || 'heads';
  const metalInfo = getSafeMetalInfo(cfg.metal);
  const previewImg = cfg.previewImage || cfg.preview_image || null;

  const isSharedAll = cfg.shared_all !== false;
  const sharedEmails = Array.isArray(cfg.shared_user_emails) ? cfg.shared_user_emails : [];
  const sharedCompanies = Array.isArray(cfg.shared_companies) ? cfg.shared_companies : (Array.isArray(cfg.shared_company_ids) ? cfg.shared_company_ids : []);
  const showSharedBadge = isSharedAll || sharedEmails.length > 0 || sharedCompanies.length > 0;

  const metalBorder = metalInfo?.border || '#ca8a04';
  const metalBg = typeof metalInfo?.bg === 'string' && metalInfo.bg.startsWith('#') ? metalInfo.bg : '#334155';
  const metalText = metalInfo?.text || '#78350f';
  const metalLabel = metalInfo?.label || (typeof cfg.metal === 'string' ? cfg.metal : 'Gold');

  const symbolStr = typeof cfg.symbol === 'string' ? cfg.symbol : '';
  const isSymbolImage = symbolStr.startsWith('http') || symbolStr.includes('.') || symbolStr.includes('/');

  return (
    <div
      data-testid={`face-template-card-${t.id}`}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      style={{
        backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'var(--card-bg, #1e1e22)',
        border: `1.5px solid ${isSelected ? 'var(--primary-color)' : 'var(--border-color)'}`,
        borderRadius: '12px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        boxShadow: isSelected ? '0 0 0 2px rgba(189, 178, 255, 0.4), 0 8px 24px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease',
        userSelect: 'none'
      }}
      onMouseOver={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseOut={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'var(--border-color)';
          e.currentTarget.style.transform = 'none';
        }
      }}
    >
      {/* TAG OVERLAY: HEADS / TAILS */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        backgroundColor: side === 'tails' ? 'rgba(37, 99, 235, 0.92)' : 'rgba(217, 119, 6, 0.92)',
        color: '#ffffff',
        fontSize: '10px',
        fontWeight: '800',
        padding: '2px 7px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <span>{side === 'tails' ? 'TAILS' : 'HEADS'}</span>
      </div>

      {/* SHARED BADGE */}
      {showSharedBadge && (
        <div
          data-testid={`face-template-shared-badge-${t.id}`}
          title={
            isSharedAll
              ? 'Shared with all tenant users'
              : sharedCompanies.length > 0
                ? `Shared with ${sharedCompanies.length} company/companies${sharedCompanies.map(c => c.name).filter(Boolean).length > 0 ? ` (${sharedCompanies.map(c => c.name || c).join(', ')})` : ''}${sharedEmails.length > 0 ? ` & ${sharedEmails.length} user(s)` : ''}`
                : `Shared with ${sharedEmails.length} user(s)`
          }
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(24, 24, 27, 0.88)',
            color: isSharedAll ? '#38bdf8' : sharedCompanies.length > 0 ? '#60a5fa' : '#38bdf8',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            width: '24px',
            height: '24px',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(6px)',
            zIndex: 5
          }}
        >
          {isSharedAll ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          ) : sharedCompanies.length > 0 ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" />
              <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
              <line x1="9" y1="8" x2="10" y2="8" />
              <line x1="9" y1="12" x2="10" y2="12" />
              <line x1="14" y1="8" x2="15" y2="8" />
              <line x1="14" y1="12" x2="15" y2="12" />
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </div>
      )}

      {/* CIRCULAR COIN PREVIEW CONTAINER */}
      <div style={{
        width: `${Math.round(140 * ((gridZoom || 100) / 100))}px`,
        height: `${Math.round(140 * ((gridZoom || 100) / 100))}px`,
        borderRadius: '50%',
        marginTop: '16px',
        marginBottom: '12px',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        background: '#141416'
      }}>
        {previewImg ? (
          <img
            key={`${t.id}-${t.updated_at || ''}-${typeof previewImg === 'string' ? previewImg.slice(-25) : ''}`}
            src={previewImg}
            alt={t.name || 'Template Preview'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          /* Dynamic fallback coin face SVG */
          <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
            <circle cx="100" cy="100" r="95" fill={metalBorder} />
            <circle cx="100" cy="100" r="85" fill={metalBg} />
            {isSymbolImage ? (
              <image href={symbolStr} x="55" y="55" width="90" height="90" />
            ) : (
              <text
                x="100"
                y="115"
                fontSize="48"
                textAnchor="middle"
                fill={metalText}
                fontWeight="bold"
              >
                {symbolStr || '🪙'}
              </text>
            )}
          </svg>
        )}
      </div>

      {/* TEMPLATE INFO */}
      <div style={{ width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span
          title={t.name || 'Untitled Face'}
          style={{
            fontSize: '12px',
            fontWeight: '700',
            color: 'var(--text-main, #f8fafc)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            width: '100%'
          }}
        >
          {t.name || 'Untitled Face'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '10px', color: 'var(--text-mute, #94a3b8)' }}>
          <span style={{ textTransform: 'capitalize' }}>{side}</span>
          <span>•</span>
          <span style={{ textTransform: 'capitalize' }}>{metalLabel}</span>
        </div>
      </div>
    </div>
  );
}

export default function FaceTemplateManager({
  user,
  tenantConfig,
  tenants,
  selectedTenant,
  selectedUserContext,
  isFullscreenFaceEditorOpen,
  refreshKey,
  onEditFace,
  onCreateNewFace
}) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const isGlobalRole = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(user?.role);
  const canExportImport = isGlobalRole || user?.role === "TENANT_ADMIN";
  const modal = useModal();

  // Manager View States
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGridTemplateId, setSelectedGridTemplateId] = useState(null);

  // Export & Import states
  const [isExporting, setIsExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importTargetTenantId, setImportTargetTenantId] = useState(selectedTenant ? selectedTenant.toString() : '');
  const [importFaceName, setImportFaceName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [tenantList, setTenantList] = useState(tenants || []);

  // Verification & Scope states for Import
  const [importVerifyReport, setImportVerifyReport] = useState(null);
  const [importVerifying, setImportVerifying] = useState(false);
  const [importIsGlobalScope, setImportIsGlobalScope] = useState(false);
  const [importAutoCreateDatasets, setImportAutoCreateDatasets] = useState(true);

  // Push to Production states
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushTemplateId, setPushTemplateId] = useState(null);
  const [pushTargetUrl, setPushTargetUrl] = useState(() => localStorage.getItem('trophycoin_prod_push_url') || 'https://trophycoin-app-cxyiczjsma-uc.a.run.app');
  const [pushRemoteToken, setPushRemoteToken] = useState(() => localStorage.getItem('trophycoin_prod_push_token') || '');
  const [pushRemoteTenants, setPushRemoteTenants] = useState([]);
  const [pushSelectedScope, setPushSelectedScope] = useState('global');
  const [pushLoadingTenants, setPushLoadingTenants] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushVerifying, setPushVerifying] = useState(false);
  const [pushVerifyReport, setPushVerifyReport] = useState(null);
  const [pushError, setPushError] = useState('');
  const [pushSuccess, setPushSuccess] = useState('');
  const [pushAutoCreateDatasets, setPushAutoCreateDatasets] = useState(true);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [faceSideFilter, setFaceSideFilter] = useState('all'); // 'all' | 'heads' | 'tails'
  const [gridZoom, setGridZoom] = useState(() => {
    const saved = localStorage.getItem('faceTemplatesGridZoom');
    return saved ? Number(saved) : 100;
  });

  const [saveStatus, setSaveStatus] = useState('');
  const [statusError, setStatusError] = useState('');

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (saveStatus) modal.notify(saveStatus, 'success');
  }, [saveStatus, modal]);

  useEffect(() => {
    if (statusError) modal.notify(statusError, 'error');
  }, [statusError, modal]);

  useEffect(() => {
    if (importSuccess) modal.notify(importSuccess, 'success');
  }, [importSuccess, modal]);

  useEffect(() => {
    if (pushSuccess) modal.notify(pushSuccess, 'success');
  }, [pushSuccess, modal]);

  // Share Modal States
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingTemplate, setSharingTemplate] = useState(null);
  const [shareAllTenantUsers, setShareAllTenantUsers] = useState(true);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [sharedCompanies, setSharedCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [sharedUserEmails, setSharedUserEmails] = useState([]);
  const [newShareEmail, setNewShareEmail] = useState('');

  // New Face Template Modal States
  const [showNewModal, setShowNewModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('New Coin Face Template');
  const [newTemplateSide, setNewTemplateSide] = useState('heads'); // 'heads' | 'tails'

  useEffect(() => {
    localStorage.setItem('faceTemplatesGridZoom', gridZoom.toString());
  }, [gridZoom]);

  const fetchFaceTemplates = async () => {
    if (!token) return;
    setLoading(true);
    try {
      let url = `${API_BASE}/api/v1/designs?design_type=coin_face&is_template=true&summary=true&_t=${Date.now()}`;
      if (selectedTenant && isGlobalRole) {
        url += `&tenant_id=${selectedTenant}`;
      }

      const res = await fetch(url, {
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter for templates (is_template !== false)
        const filtered = Array.isArray(data) ? data.filter(d => {
          if (!d) return false;
          const cfg = safeParseConfig(d.config);
          return cfg.is_template !== false;
        }) : [];
        setTemplates(filtered);
      }
    } catch (err) {
      console.error('Failed to load coin face templates list', err);
      setStatusError('Failed to load coin face templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaceTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedTenant, refreshKey]);

  const prevEditorOpenRef = useRef(isFullscreenFaceEditorOpen);
  useEffect(() => {
    if (prevEditorOpenRef.current && !isFullscreenFaceEditorOpen) {
      fetchFaceTemplates();
    }
    prevEditorOpenRef.current = isFullscreenFaceEditorOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreenFaceEditorOpen]);

  const handleOpenNewModal = () => {
    setNewTemplateName(`Coin Face Template #${templates.length + 1}`);
    setNewTemplateSide('heads');
    setShowNewModal(true);
  };

  const handleConfirmCreateNew = async () => {
    if (!newTemplateName.trim()) {
      alert('Please enter a valid template name.');
      return;
    }
    setShowNewModal(false);

    if (onCreateNewFace) {
      await onCreateNewFace(newTemplateName.trim(), newTemplateSide, 'gold');
    } else {
      // Internal creation fallback
      const defaultRings = [
        {
          id: 1,
          name: 'Base Disk',
          outerRadiusX: 95,
          outerRadiusY: 95,
          innerRadiusX: 0,
          innerRadiusY: 0,
          offsetX: 0,
          offsetY: 0,
          grayscale: 0,
          outerBlur: 0,
          innerBlur: 0,
          noise: 0,
          noiseSize: 50,
          patternUrl: '',
          locked: false,
          constrainOuter: true,
          constrainInner: true,
          lockOffset: false
        }
      ];

      const defaultConfig = {
        metal: 'gold',
        side: newTemplateSide,
        symbol: '',
        upperText: '',
        lowerText: '',
        textMode: 'split',
        singleText: '',
        textFontWeight: 'normal',
        textFontStyle: 'normal',
        textCase: 'uppercase',
        edgeStyle: 'reeded',
        depth: 'medium',
        textGrayscale: 60,
        textRadius: 68,
        textSize: 16,
        textKerning: 1.2,
        textFont: '"Arial Black", sans-serif',
        rings: defaultRings,
        selectedRingId: 1,
        is_template: true,
        shared_all: true,
        shared_user_emails: []
      };

      let associatedId = user.id;
      if (selectedUserContext && selectedUserContext !== 'self') {
        associatedId = parseInt(selectedUserContext);
      }

      const payload = {
        name: newTemplateName.trim(),
        design_type: 'coin_face',
        config: defaultConfig,
        associated_user_id: associatedId,
        tenant_id: isGlobalRole ? (selectedTenant ? parseInt(selectedTenant) : null) : parseInt(tenantConfig.id)
      };

      try {
        const dRes = await fetch(`${API_BASE}/api/v1/designs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(payload)
        });
        const designData = await dRes.json();
        if (dRes.ok && onEditFace) {
          fetchFaceTemplates();
          onEditFace(designData);
        }
      } catch (err) {
        console.error('Error creating face template', err);
        alert('Failed to create face template.');
      }
    }
  };

  const handleDuplicateTemplate = async (design, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const name = await modal.prompt('Enter a name for the duplicated face template:', `${design.name} (Copy)`, {
      title: 'Duplicate Face Template',
      confirmText: 'Duplicate'
    });
    if (!name) return;

    const payload = {
      name: name.trim(),
      design_type: 'coin_face',
      config: {
        ...design.config,
        is_template: true
      },
      associated_user_id: user?.id || 1,
      tenant_id: design.tenant_id || (isGlobalRole ? (selectedTenant ? parseInt(selectedTenant) : null) : parseInt(tenantConfig.id))
    };

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setSaveStatus(`Cloned template "${name}" successfully!`);
        fetchFaceTemplates();
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        const errData = await res.json();
        modal.alert(errData.detail || 'Failed to clone face template.', { type: 'error', title: 'Clone Failed' });
      }
    } catch (err) {
      console.error('Error duplicating template', err);
      modal.alert('Error occurred while cloning template.', { type: 'error', title: 'Clone Error' });
    }
  };

  const handleDeleteTemplate = async (templateId, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const target = templates.find(t => t.id === templateId);
    if (!target) return;

    const confirmed = await modal.confirm(`Are you sure you want to delete the face template "${target.name}"?`, {
      title: 'Delete Face Template',
      danger: true,
      confirmText: 'Delete'
    });
    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${templateId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        setSaveStatus(`Template "${target.name}" deleted.`);
        setSelectedGridTemplateId(null);
        fetchFaceTemplates();
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        const errData = await res.json();
        alert(errData.detail || 'Failed to delete template.');
      }
    } catch (err) {
      console.error('Error deleting template', err);
      alert('Error occurred while deleting template.');
    }
  };

  const fetchAvailableCompanies = async () => {
    if (!token) return;
    try {
      let url = `${API_BASE}/api/v1/organizations`;
      if (selectedTenant && isGlobalRole) {
        url += `?tenant_id=${selectedTenant}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        const orgs = Array.isArray(data) ? data : (data && Array.isArray(data.organizations) ? data.organizations : []);
        setAvailableCompanies(orgs);
        if (!tenants || tenants.length === 0) {
          setTenantList(orgs);
        }
      }
    } catch (err) {
      console.error('Failed to load companies for sharing', err);
    }
  };

  const handleOpenShareModal = (template) => {
    setSharingTemplate(template);
    const config = template.config || {};
    setShareAllTenantUsers(config.shared_all !== false);

    let initialCompanies = [];
    if (Array.isArray(config.shared_companies) && config.shared_companies.length > 0) {
      initialCompanies = config.shared_companies.map(c => typeof c === 'object' && c !== null ? c : { id: c, name: `Company #${c}` });
    } else if (Array.isArray(config.shared_company_ids) && config.shared_company_ids.length > 0) {
      initialCompanies = config.shared_company_ids.map(id => {
        const found = availableCompanies.find(c => c.id === id);
        return found ? { id: found.id, name: found.name } : { id, name: `Company #${id}` };
      });
    }
    setSharedCompanies(initialCompanies);
    setSelectedCompanyId('');
    setSharedUserEmails(config.shared_user_emails || []);
    setNewShareEmail('');
    fetchAvailableCompanies();
    setShowShareModal(true);
  };

  const handleAddCompany = (compIdToAdd) => {
    const compId = compIdToAdd !== undefined ? compIdToAdd : parseInt(selectedCompanyId);
    if (!compId) return;
    const compObj = availableCompanies.find(c => c.id === compId);
    const name = compObj ? compObj.name : `Company #${compId}`;
    if (!sharedCompanies.some(c => c.id === compId)) {
      setSharedCompanies([...sharedCompanies, { id: compId, name }]);
    }
    setSelectedCompanyId('');
  };

  const handleRemoveCompany = (compIdToRemove) => {
    setSharedCompanies(sharedCompanies.filter(c => c.id !== compIdToRemove));
  };

  const handleSaveShareSettings = async () => {
    if (!sharingTemplate) return;

    const updatedConfig = {
      ...(sharingTemplate.config || {}),
      shared_all: shareAllTenantUsers,
      shared_companies: sharedCompanies,
      shared_company_ids: sharedCompanies.map(c => c.id),
      shared_user_emails: sharedUserEmails
    };

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${sharingTemplate.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          config: updatedConfig
        })
      });

      if (res.ok) {
        fetchFaceTemplates();
        setShowShareModal(false);
        setSaveStatus('Template share settings updated successfully!');
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to update share settings.');
      }
    } catch (err) {
      console.error(err);
      alert('Error occurred while saving share settings.');
    }
  };

  useEffect(() => {
    if (tenants && Array.isArray(tenants) && tenants.length > 0) {
      setTenantList(tenants);
    }
    fetchAvailableCompanies();
  }, [tenants, selectedTenant, isGlobalRole, token, API_BASE]);

  const handleExportFace = async (templateId) => {
    const idToExport = templateId || selectedGridTemplateId;
    if (!idToExport) return;
    setIsExporting(true);
    setStatusError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${idToExport}/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to export coin face template.');
      }
      const data = await res.json();
      const faceName = data.face?.name || data.card?.name || 'coin_face';
      const safeName = faceName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeName}_coin_face.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting coin face:', err);
      alert(err.message || 'Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  const runImportVerification = async (dataToVerify, targetTenant, isGlobal) => {
    if (!dataToVerify) {
      setImportVerifyReport(null);
      return;
    }
    setImportVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/import/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_tenant_id: isGlobal ? null : (targetTenant ? parseInt(targetTenant, 10) : null),
          is_global_scope: Boolean(isGlobal),
          export_data: dataToVerify
        })
      });
      if (res.ok) {
        const report = await res.json();
        setImportVerifyReport(report);
      } else {
        setImportVerifyReport(null);
      }
    } catch (err) {
      console.error("Failed to verify import data:", err);
      setImportVerifyReport(null);
    } finally {
      setImportVerifying(false);
    }
  };

  const handleOpenImportModal = () => {
    setImportData(null);
    setImportFileName('');
    setImportFaceName('');
    setImportError('');
    setImportSuccess('');
    setImportVerifyReport(null);
    setImportIsGlobalScope(false);
    setImportAutoCreateDatasets(true);
    if (selectedTenant) {
      setImportTargetTenantId(selectedTenant.toString());
    } else if (tenantList.length > 0) {
      setImportTargetTenantId(tenantList[0].id.toString());
    }
    setShowImportModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportError('');
    setImportSuccess('');
    setImportVerifyReport(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        const validSchemas = ['trophycoin_face_v1', 'trophycoin_card_v1', 'trophycoin_card_v2', 'trophycoin_template_v1'];
        const templateObj = parsed.face || parsed.card || parsed.template;
        if (!validSchemas.includes(parsed.schema_version) || !templateObj) {
          setImportError("Invalid export file. Expected schema_version 'trophycoin_face_v1' or 'trophycoin_card_v1' and template configuration.");
          setImportData(null);
          return;
        }
        setImportData(parsed);
        setImportFaceName(templateObj.name ? `${templateObj.name} (Copy)` : 'Imported Coin Face');
        runImportVerification(parsed, importTargetTenantId, importIsGlobalScope);
      } catch (err) {
        setImportError("Could not parse file. Ensure it is valid JSON.");
        setImportData(null);
      }
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    if (!importData) {
      setImportError('Please select a valid template file first.');
      return;
    }
    if (!importIsGlobalScope && !importTargetTenantId) {
      setImportError('Please select a target tenant organization.');
      return;
    }

    setImportLoading(true);
    setImportError('');
    setImportSuccess('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_tenant_id: importIsGlobalScope ? null : parseInt(importTargetTenantId, 10),
          is_global_scope: Boolean(importIsGlobalScope),
          card_name: importFaceName.trim() || undefined,
          template_name: importFaceName.trim() || undefined,
          auto_create_datasets: Boolean(importAutoCreateDatasets),
          export_data: importData
        })
      });

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.detail || 'Import failed.');
      }

      setImportSuccess(resData.message || 'Coin face template imported successfully!');
      fetchFaceTemplates();
      if (resData.design?.id) {
        setSelectedGridTemplateId(resData.design.id);
      }
      setTimeout(() => {
        setShowImportModal(false);
        setImportSuccess('');
      }, 1500);
    } catch (err) {
      console.error('Import error:', err);
      setImportError(err.message || 'An error occurred during import.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenPushModal = (templateId) => {
    const id = templateId || selectedGridTemplateId;
    if (!id) return;
    setPushTemplateId(id);
    setPushError('');
    setPushSuccess('');
    setPushVerifyReport(null);
    setPushSelectedScope('global');
    setShowPushModal(true);
    if (pushRemoteToken) {
      handleFetchRemoteTenants(pushTargetUrl, pushRemoteToken);
    }
  };

  const handleFetchRemoteTenants = async (urlToUse, tokenToUse) => {
    const url = (urlToUse || pushTargetUrl).trim().replace(/\/+$/, '');
    const tok = (tokenToUse || pushRemoteToken).trim();
    if (!url || !tok) return;

    setPushLoadingTenants(true);
    setPushError('');
    try {
      localStorage.setItem('trophycoin_prod_push_url', url);
      localStorage.setItem('trophycoin_prod_push_token', tok);

      const res = await fetch(`${API_BASE}/api/v1/designs/push/remote-tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          remote_url: url,
          remote_token: tok
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not connect to remote server.');
      }

      const tenants = await res.json();
      setPushRemoteTenants(Array.isArray(tenants) ? tenants : []);
    } catch (err) {
      console.error('Failed to fetch remote tenants:', err);
      setPushError(`Remote connection error: ${err.message}`);
      setPushRemoteTenants([]);
    } finally {
      setPushLoadingTenants(false);
    }
  };

  const handleVerifyPush = async () => {
    const url = pushTargetUrl.trim().replace(/\/+$/, '');
    const tok = pushRemoteToken.trim();
    if (!url) {
      setPushError('Please enter a target remote server URL.');
      return;
    }
    if (!tok) {
      setPushError('Please enter an authentication token for the destination server.');
      return;
    }

    setPushVerifying(true);
    setPushError('');
    setPushVerifyReport(null);

    const isGlobal = pushSelectedScope === 'global';
    const targetTenantId = !isGlobal && pushSelectedScope !== '' ? parseInt(pushSelectedScope, 10) : null;

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${pushTemplateId}/push/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          remote_url: url,
          remote_token: tok,
          target_tenant_id: targetTenantId,
          is_global_scope: isGlobal
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Remote verification failed.');
      }

      const report = await res.json();
      setPushVerifyReport(report);
    } catch (err) {
      console.error('Remote verification error:', err);
      setPushError(`Verification failed: ${err.message}`);
      setPushVerifyReport(null);
    } finally {
      setPushVerifying(false);
    }
  };

  const handleExecutePush = async () => {
    const url = pushTargetUrl.trim().replace(/\/+$/, '');
    const tok = pushRemoteToken.trim();
    if (!url || !tok) {
      setPushError('Remote server URL and token are required.');
      return;
    }

    const isGlobal = pushSelectedScope === 'global';
    const targetTenantId = !isGlobal && pushSelectedScope !== '' ? parseInt(pushSelectedScope, 10) : null;

    setPushLoading(true);
    setPushError('');
    setPushSuccess('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${pushTemplateId}/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          remote_url: url,
          remote_token: tok,
          target_tenant_id: targetTenantId,
          is_global_scope: isGlobal,
          auto_create_datasets: Boolean(pushAutoCreateDatasets)
        })
      });

      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.detail || 'Push execution failed.');
      }

      setPushSuccess(resData.message || 'Coin face template pushed to remote server successfully!');
      setTimeout(() => {
        setShowPushModal(false);
        setPushSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Push error:', err);
      setPushError(`Push error: ${err.message}`);
    } finally {
      setPushLoading(false);
    }
  };

  // Filter templates based on side and search query
  const filteredTemplates = (Array.isArray(templates) ? templates : []).filter(t => {
    if (!t) return false;
    const nameStr = (t.name || '').toLowerCase();
    // Search query filter
    if (templateSearchQuery && !nameStr.includes(templateSearchQuery.toLowerCase())) {
      return false;
    }
    // Face side filter
    const cfg = safeParseConfig(t.config);
    const side = (cfg.side || 'heads').toLowerCase();
    if (faceSideFilter === 'heads') {
      return side === 'heads';
    }
    if (faceSideFilter === 'tails') {
      return side === 'tails';
    }
    return true;
  });

  const renderImportModal = () => {
    if (!showImportModal) return null;

    const templateObj = importData?.face || importData?.card || importData?.template;
    const sourceTenantName = importData?.source_tenant?.brand_name || 'N/A (Generic / Global)';
    const ringsCount = (templateObj?.config?.rings || []).length;
    const metal = templateObj?.config?.metal || 'gold';
    const side = templateObj?.config?.side || 'heads';
    const bundledAssets = importData?.assets || [];

    return createPortal(
      <div 
        data-testid="import-face-modal-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          width: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-color, #2d2d30)',
            padding: '16px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>📥</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', letterSpacing: '0.3px' }}>
                Import Coin Face Template & Assets
              </span>
            </div>
            <button 
              onClick={() => setShowImportModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            color: '#dfdfe3'
          }}>
            {/* Target Scope & Tenant Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Target Scope & Template Library
              </label>
              {isGlobalRole ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: '#dfdfe3' }}>
                      <input 
                        type="radio" 
                        name="importScope" 
                        checked={!importIsGlobalScope} 
                        onChange={() => {
                          setImportIsGlobalScope(false);
                          if (importData) runImportVerification(importData, importTargetTenantId, false);
                        }}
                      />
                      Tenant Organization
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: '#dfdfe3' }}>
                      <input 
                        type="radio" 
                        name="importScope" 
                        checked={importIsGlobalScope} 
                        onChange={() => {
                          setImportIsGlobalScope(true);
                          if (importData) runImportVerification(importData, null, true);
                        }}
                      />
                      ⭐ Global System Template (All Tenants)
                    </label>
                  </div>

                  {!importIsGlobalScope && (
                    <select
                      data-testid="import-tenant-select"
                      value={importTargetTenantId}
                      onChange={(e) => {
                        setImportTargetTenantId(e.target.value);
                        if (importData) runImportVerification(importData, e.target.value, false);
                      }}
                      style={{
                        backgroundColor: '#27272a',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#f4f4f5',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    >
                      {tenantList.map(t => (
                        <option key={t.id} value={t.id.toString()}>
                          {t.brand_name || t.domain_name} (ID: {t.id})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#a1a1aa' }}>
                  Importing into: <strong style={{ color: '#f4f4f5' }}>{tenantConfig?.brand_name || 'Current Organization'}</strong>
                </div>
              )}
            </div>

            {/* File Drop / Select Area */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Select Coin Face Template File
              </label>
              <label style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #3f3f46',
                borderRadius: '8px',
                padding: '24px 16px',
                cursor: 'pointer',
                backgroundColor: '#18181b',
                transition: 'all 0.2s',
                gap: '8px'
              }}>
                <span style={{ fontSize: '28px' }}>🪙</span>
                <span style={{ fontSize: '13px', color: '#a1a1aa' }}>
                  {importFileName ? importFileName : 'Click to browse or drop .json / .trophyface file'}
                </span>
                <span style={{ fontSize: '11px', color: '#71717a' }}>
                  Compatible with schema_version: trophycoin_face_v1 or trophycoin_card_v1
                </span>
                <input 
                  type="file" 
                  accept=".json,.trophyface,.trophycard"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {/* Imported Template Name input */}
            {importData && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Template Name in Target Library
                </label>
                <input
                  type="text"
                  data-testid="import-face-name-input"
                  value={importFaceName}
                  onChange={(e) => setImportFaceName(e.target.value)}
                  placeholder="e.g. Fisherman's Crest Heads"
                  style={{
                    backgroundColor: '#27272a',
                    border: '1px solid #3f3f46',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#f4f4f5',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
            )}

            {/* Template Summary Card */}
            {importData && (
              <div style={{
                backgroundColor: '#18181b',
                border: '1px solid #27272a',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#e4e4e7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Template Summary</span>
                  <span style={{ 
                    fontSize: '11px', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    backgroundColor: side === 'heads' ? 'rgba(217, 119, 6, 0.2)' : 'rgba(37, 99, 235, 0.2)', 
                    color: side === 'heads' ? '#f59e0b' : '#60a5fa',
                    fontWeight: '600'
                  }}>
                    {side.toUpperCase()} • {metal.toUpperCase()}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#a1a1aa' }}>
                  <div>Source Tenant: <span style={{ color: '#f4f4f5', fontWeight: '500' }}>{sourceTenantName}</span></div>
                  <div>Rings / Layers: <span style={{ color: '#f4f4f5', fontWeight: '500' }}>{ringsCount}</span></div>
                  <div>Bundled Assets: <span style={{ color: '#f4f4f5', fontWeight: '500' }}>{bundledAssets.length} image(s)</span></div>
                  <div>Datasets: <span style={{ color: '#f4f4f5', fontWeight: '500' }}>{(importData?.datasets || []).length}</span></div>
                </div>
              </div>
            )}

            {/* Verification Preflight Report */}
            {importVerifying && (
              <div style={{ fontSize: '12px', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="spinner" style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid #60a5fa', borderTopColor: 'transparent', borderRadius: '50%' }}></span>
                Verifying template structure and target scope compatibility...
              </div>
            )}

            {importVerifyReport && (
              <div 
                data-testid="import-dataset-verification"
                style={{
                  backgroundColor: '#18181b',
                  border: '1px solid #3f3f46',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#e4e4e7' }}>
                    Preflight Verification: {importVerifyReport.target_scope}
                  </span>
                  <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>
                    Ready to Import
                  </span>
                </div>

                {/* Auto-create datasets toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#dfdfe3', cursor: 'pointer', marginTop: '4px' }}>
                  <input
                    type="checkbox"
                    checked={importAutoCreateDatasets}
                    onChange={(e) => setImportAutoCreateDatasets(e.target.checked)}
                  />
                  <span>Auto-create missing datasets & fields in target environment</span>
                </label>
              </div>
            )}

            {/* Error & Success Messages */}
            {importError && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                padding: '10px 14px',
                color: '#fca5a5',
                fontSize: '12px'
              }}>
                ✕ {importError}
              </div>
            )}

            {importSuccess && (
              <div style={{
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid #10b981',
                borderRadius: '6px',
                padding: '10px 14px',
                color: '#6ee7b7',
                fontSize: '12px'
              }}>
                ✓ {importSuccess}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            borderTop: '1px solid var(--border-color, #2d2d30)',
            padding: '14px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <button
              onClick={() => setShowImportModal(false)}
              disabled={importLoading}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color, #3f3f46)',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'var(--text-mute, #a1a1aa)',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              data-testid="execute-import-btn"
              onClick={handleExecuteImport}
              disabled={!importData || importLoading}
              style={{
                background: importData && !importLoading ? 'var(--primary-gradient, var(--primary-color, #3b82f6))' : 'var(--disabled-bg, #27272a)',
                color: importData && !importLoading ? '#ffffff' : 'var(--disabled-text, #71717a)',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: importData && !importLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {importLoading ? 'Importing...' : 'Execute Import'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderPushModal = () => {
    if (!showPushModal) return null;

    return createPortal(
      <div 
        data-testid="push-face-modal-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          width: '580px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-color, #2d2d30)',
            padding: '16px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🚀</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', letterSpacing: '0.3px' }}>
                Push Coin Face to Production / Remote Server
              </span>
            </div>
            <button 
              onClick={() => setShowPushModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            color: '#dfdfe3'
          }}>
            {/* Target Remote URL */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                Remote Destination Server URL
              </label>
              <input
                type="text"
                data-testid="push-target-url-input"
                value={pushTargetUrl}
                onChange={(e) => setPushTargetUrl(e.target.value)}
                placeholder="https://trophycoin-app-cxyiczjsma-uc.a.run.app"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#f4f4f5',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Remote Auth Token */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                  Destination Auth Token (Bearer Token / Operator Token)
                </label>
                <button
                  type="button"
                  data-testid="push-fetch-tenants-btn"
                  onClick={() => handleFetchRemoteTenants(pushTargetUrl, pushRemoteToken)}
                  disabled={pushLoadingTenants || !pushRemoteToken}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: pushRemoteToken ? '#60a5fa' : '#71717a',
                    fontSize: '11px',
                    cursor: pushRemoteToken ? 'pointer' : 'not-allowed',
                    textDecoration: 'underline'
                  }}
                >
                  {pushLoadingTenants ? 'Connecting...' : 'Connect & Fetch Tenants'}
                </button>
              </div>
              <input
                type="password"
                data-testid="push-remote-token-input"
                value={pushRemoteToken}
                onChange={(e) => setPushRemoteToken(e.target.value)}
                placeholder="Paste destination user token or operator bypass token"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#f4f4f5',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Target Scope Selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                Target Scope on Destination Server
              </label>
              <select
                data-testid="push-scope-select"
                value={pushSelectedScope}
                onChange={(e) => setPushSelectedScope(e.target.value)}
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#f4f4f5',
                  fontSize: '13px',
                  outline: 'none'
                }}
              >
                <option value="global">⭐ Global Scope (System Template across all tenants)</option>
                {pushRemoteTenants.map(t => (
                  <option key={t.id} value={t.id.toString()}>
                    🏢 Tenant: {t.brand_name || t.domain_name} (ID: {t.id})
                  </option>
                ))}
              </select>
            </div>

            {/* Auto-create datasets toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#dfdfe3', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={pushAutoCreateDatasets}
                onChange={(e) => setPushAutoCreateDatasets(e.target.checked)}
              />
              <span>Auto-create missing datasets & fields on destination server</span>
            </label>

            {/* Verify Button */}
            <div>
              <button
                type="button"
                data-testid="push-verify-btn"
                onClick={handleVerifyPush}
                disabled={pushVerifying || !pushRemoteToken}
                style={{
                  background: pushRemoteToken && !pushVerifying ? '#1e293b' : 'var(--disabled-bg, #18181b)',
                  color: pushRemoteToken ? '#93c5fd' : 'var(--disabled-text, #71717a)',
                  border: '1px solid ' + (pushRemoteToken ? '#3b82f6' : 'var(--border-color)'),
                  borderRadius: '6px',
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: pushRemoteToken && !pushVerifying ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>🔍</span>
                <span>{pushVerifying ? 'Running Preflight Verification...' : 'Verify Connection & Scopes'}</span>
              </button>
            </div>

            {/* Verification Preflight Report Card */}
            {pushVerifyReport && (
              <div 
                data-testid="push-verify-report"
                style={{
                  backgroundColor: '#18181b',
                  border: '1px solid #3f3f46',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#e4e4e7' }}>
                    Preflight Passed: {pushVerifyReport.card_name}
                  </span>
                  <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>
                    ✓ Compatible
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#a1a1aa' }}>
                  Scope: <strong style={{ color: '#f4f4f5' }}>{pushVerifyReport.target_scope}</strong> • Assets: <strong style={{ color: '#f4f4f5' }}>{pushVerifyReport.assets_count}</strong>
                </div>
              </div>
            )}

            {/* Error Banner */}
            {pushError && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                padding: '10px 14px',
                color: '#fca5a5',
                fontSize: '12px'
              }}>
                ✕ {pushError}
              </div>
            )}

            {/* Success Banner */}
            {pushSuccess && (
              <div style={{
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid #10b981',
                borderRadius: '6px',
                padding: '10px 14px',
                color: '#6ee7b7',
                fontSize: '12px'
              }}>
                ✓ {pushSuccess}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            borderTop: '1px solid var(--border-color, #2d2d30)',
            padding: '14px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <button
              onClick={() => setShowPushModal(false)}
              disabled={pushLoading}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color, #3f3f46)',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'var(--text-mute, #a1a1aa)',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              data-testid="push-execute-btn"
              onClick={handleExecutePush}
              disabled={!pushRemoteToken || pushLoading}
              style={{
                background: pushRemoteToken && !pushLoading ? 'var(--primary-gradient, var(--primary-color, #3b82f6))' : 'var(--disabled-bg, #27272a)',
                color: pushRemoteToken && !pushLoading ? '#ffffff' : 'var(--disabled-text, #71717a)',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: pushRemoteToken && !pushLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {pushLoading ? 'Pushing to Remote...' : 'Push Coin Face to Production'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div style={{ color: 'var(--text-main)', width: '100%', boxSizing: 'border-box' }}>
      {/* Notifications Bar */}
      {saveStatus && (
        <div style={{
          backgroundColor: '#10b981',
          color: 'white',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>✓</span>
          <span>{saveStatus}</span>
        </div>
      )}
      {statusError && (
        <div style={{
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '8px 16px',
          fontSize: '12px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>⚠️</span>
          <span>{statusError}</span>
        </div>
      )}

      {/* Main Toolbar Panel */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
        backgroundColor: 'transparent',
        borderBottom: '1px solid var(--border-color)',
        padding: '12px 16px'
      }}>
        {/* NEW FACE TEMPLATE BUTTON */}
        <button
          type="button"
          onClick={handleOpenNewModal}
          style={{
            backgroundColor: '#ffffff',
            color: '#18181b',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            padding: '0 16px',
            height: '36px',
            boxSizing: 'border-box',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>New Face</span>
        </button>

        <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-color)', margin: '0 4px' }} />

        {/* EDIT BUTTON */}
        <button
          type="button"
          disabled={!selectedGridTemplateId}
          onClick={() => {
            const selectedT = templates.find(t => t.id === selectedGridTemplateId);
            if (selectedT && onEditFace) onEditFace(selectedT);
          }}
          title="Edit Face Template in Fullscreen Editor"
          style={{
            background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
            color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
            border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
            borderRadius: '6px',
            padding: '0 10px',
            height: '36px',
            boxSizing: 'border-box',
            fontSize: '12px',
            fontWeight: '600',
            cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            opacity: selectedGridTemplateId ? 1 : 0.85
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>

        {/* DUPLICATE BUTTON */}
        <button
          type="button"
          disabled={!selectedGridTemplateId}
          onClick={(e) => {
            const selectedT = templates.find(t => t.id === selectedGridTemplateId);
            if (selectedT) handleDuplicateTemplate(selectedT, e);
          }}
          title="Duplicate Face Template"
          style={{
            background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
            color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
            border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
            borderRadius: '6px',
            padding: '0 10px',
            height: '36px',
            boxSizing: 'border-box',
            fontSize: '12px',
            fontWeight: '600',
            cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            opacity: selectedGridTemplateId ? 1 : 0.85
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>

        {/* DELETE BUTTON */}
        <button
          type="button"
          disabled={!selectedGridTemplateId}
          onClick={(e) => {
            if (selectedGridTemplateId) {
              handleDeleteTemplate(selectedGridTemplateId, e);
            }
          }}
          title="Delete Face Template"
          style={{
            background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
            color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
            border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
            borderRadius: '6px',
            padding: '0 10px',
            height: '36px',
            boxSizing: 'border-box',
            fontSize: '12px',
            fontWeight: '600',
            cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            opacity: selectedGridTemplateId ? 1 : 0.85
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>

        {/* SHARE BUTTON */}
        <button
          type="button"
          disabled={!selectedGridTemplateId}
          onClick={() => {
            if (selectedGridTemplateId) {
              const selectedT = templates.find(t => t.id === selectedGridTemplateId);
              if (selectedT) handleOpenShareModal(selectedT);
            }
          }}
          title="Share Face Template Access"
          style={{
            background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
            color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
            border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
            borderRadius: '6px',
            padding: '0 10px',
            height: '36px',
            boxSizing: 'border-box',
            fontSize: '12px',
            fontWeight: '600',
            cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s',
            opacity: selectedGridTemplateId ? 1 : 0.85
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" />
            <line x1="16" y1="11" x2="22" y2="11" />
          </svg>
        </button>

        {canExportImport && (
          <>
            <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-color)', margin: '0 4px' }} />

            <button
              type="button"
              disabled={!selectedGridTemplateId || isExporting}
              onClick={() => handleExportFace(selectedGridTemplateId)}
              title="Export Coin Face Template with Textures & Geometry"
              data-testid="export-face-btn"
              style={{
                background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
                color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
                border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
                borderRadius: '6px',
                padding: '0 12px',
                height: '36px',
                boxSizing: 'border-box',
                fontSize: '12px',
                fontWeight: '600',
                cursor: selectedGridTemplateId && !isExporting ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                opacity: selectedGridTemplateId && !isExporting ? 1 : 0.85
              }}
            >
              <span style={{ fontSize: '14px' }}>📤</span>
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </button>

            <button
              type="button"
              onClick={handleOpenImportModal}
              title="Import Coin Face JSON & Textures into Template Library"
              data-testid="import-face-btn"
              style={{
                backgroundColor: '#ffffff',
                color: '#18181b',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '0 12px',
                height: '36px',
                boxSizing: 'border-box',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: '14px' }}>📥</span>
              <span>Import Face</span>
            </button>

            <button
              type="button"
              disabled={!selectedGridTemplateId}
              onClick={() => handleOpenPushModal(selectedGridTemplateId)}
              title="Push selected face directly to Production / Remote server"
              data-testid="push-face-btn"
              style={{
                background: selectedGridTemplateId ? '#3b82f6' : 'var(--disabled-bg, #000000)',
                color: selectedGridTemplateId ? '#ffffff' : 'var(--disabled-text, #5f5f65)',
                border: '1px solid ' + (selectedGridTemplateId ? '#60a5fa' : 'var(--border-color)'),
                borderRadius: '6px',
                padding: '0 12px',
                height: '36px',
                boxSizing: 'border-box',
                fontSize: '12px',
                fontWeight: '600',
                cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                opacity: selectedGridTemplateId ? 1 : 0.85
              }}
            >
              <span style={{ fontSize: '14px' }}>🚀</span>
              <span>Push to Prod</span>
            </button>
          </>
        )}

        <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-color)', margin: '0 4px' }} />

        {/* SIDE FILTER SELECTOR (Heads / Tails / Both) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'var(--panel-color, var(--card-bg, #1e1e21))',
          borderRadius: '6px',
          border: '1px solid var(--border-color)',
          padding: '2px',
          height: '36px',
          boxSizing: 'border-box'
        }}>
          <button
            type="button"
            onClick={() => setFaceSideFilter('all')}
            style={{
              backgroundColor: faceSideFilter === 'all' ? 'var(--primary-color)' : 'transparent',
              color: faceSideFilter === 'all' ? '#ffffff' : 'var(--text-mute)',
              border: 'none',
              borderRadius: '4px',
              height: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 12px',
              minWidth: '85px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box'
            }}
          >
            All Faces
          </button>
          <button
            type="button"
            onClick={() => setFaceSideFilter('heads')}
            style={{
              backgroundColor: faceSideFilter === 'heads' ? '#d97706' : 'transparent',
              color: faceSideFilter === 'heads' ? '#ffffff' : 'var(--text-mute)',
              border: 'none',
              borderRadius: '4px',
              height: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 12px',
              minWidth: '85px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box'
            }}
          >
            Heads Only
          </button>
          <button
            type="button"
            onClick={() => setFaceSideFilter('tails')}
            style={{
              backgroundColor: faceSideFilter === 'tails' ? '#2563eb' : 'transparent',
              color: faceSideFilter === 'tails' ? '#ffffff' : 'var(--text-mute)',
              border: 'none',
              borderRadius: '4px',
              height: '100%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 12px',
              minWidth: '85px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box'
            }}
          >
            Tails Only
          </button>
        </div>

        {/* Zoom Slider Control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', marginRight: '12px', height: '36px' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Zoom:</span>
          <input
            type="range"
            min="60"
            max="140"
            value={gridZoom}
            onChange={(e) => setGridZoom(Number(e.target.value))}
            style={{
              width: '90px',
              accentColor: 'var(--primary-color)',
              cursor: 'pointer'
            }}
          />
          <span style={{ fontSize: '11px', color: 'var(--text-mute)', width: '30px', textAlign: 'right' }}>{gridZoom}%</span>
        </div>

        {/* Search Input Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563', position: 'absolute', left: '10px' }}>
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search face templates..."
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            style={{
              backgroundColor: '#ffffff',
              color: '#18181b',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '0 12px 0 30px',
              height: '36px',
              boxSizing: 'border-box',
              fontSize: '12px',
              outline: 'none',
              width: '180px',
              transition: 'border-color 0.15s'
            }}
          />
        </div>
      </div>

      {/* Grid Content Body */}
      <div style={{ padding: '16px' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-mute)' }}>
            Loading coin face templates...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: 'var(--text-mute)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ fontSize: '42px' }}>🪙</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-main)' }}>No Coin Face Templates Found</div>
            <p style={{ margin: 0, fontSize: '13px', maxWidth: '380px', lineHeight: '1.5' }}>
              {templateSearchQuery || faceSideFilter !== 'all'
                ? 'No face templates match your active search or side filters.'
                : 'Click "New Face" above to design your first reusable Heads or Tails coin face template.'}
            </p>
            <button
              type="button"
              onClick={handleOpenNewModal}
              style={{
                marginTop: '8px',
                backgroundColor: 'var(--primary-color)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              + Create New Face Template
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(210 * (gridZoom / 100))}px, 1fr))`,
            gap: '16px',
            marginTop: '4px'
          }}>
            {filteredTemplates.map(t => {
              const isSelected = selectedGridTemplateId === t.id;

              return (
                <ErrorBoundary
                  key={t.id}
                  fallback={(err) => (
                    <div
                      data-testid={`face-template-error-${t.id}`}
                      onClick={() => setSelectedGridTemplateId(isSelected ? null : t.id)}
                      style={{
                        backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'var(--card-bg, #1e1e22)',
                        border: `1.5px solid ${isSelected ? 'var(--primary-color)' : '#7f1d1d'}`,
                        borderRadius: '12px',
                        padding: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: `${Math.round(200 * (gridZoom / 100))}px`,
                        cursor: 'pointer',
                        position: 'relative',
                        boxShadow: isSelected ? '0 0 0 2px rgba(239, 68, 68, 0.4), 0 8px 24px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      <div style={{
                        width: `${Math.round(70 * (gridZoom / 100))}px`,
                        height: `${Math.round(70 * (gridZoom / 100))}px`,
                        borderRadius: '50%',
                        backgroundColor: '#271214',
                        border: '1px solid #7f1d1d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '10px'
                      }}>
                        <span style={{ fontSize: '24px' }}>⚠️</span>
                      </div>
                      <span
                        title={t.name || 'Corrupted Template'}
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#f87171',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          textAlign: 'center'
                        }}
                      >
                        {t.name || 'Untitled Template'}
                      </span>
                      <span style={{ fontSize: '10px', color: '#fca5a5', marginTop: '2px' }}>
                        Preview Unavailable
                      </span>
                    </div>
                  )}
                >
                  <FaceTemplateCard
                    template={t}
                    isSelected={isSelected}
                    gridZoom={gridZoom}
                    onSelect={() => setSelectedGridTemplateId(isSelected ? null : t.id)}
                    onDoubleClick={() => {
                      if (onEditFace) onEditFace(t);
                    }}
                  />
                </ErrorBoundary>
              );
            })}
          </div>
        )}
      </div>

      {/* NEW COIN FACE TEMPLATE MODAL */}
      {showNewModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          backdropFilter: 'blur(4px)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            background: 'var(--card-bg, #1e1e22)',
            border: '1px solid var(--border-color, #3c3c42)',
            borderRadius: '12px',
            width: '420px',
            maxWidth: '90vw',
            padding: '24px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            color: 'var(--text-main, #ffffff)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #3c3c42)', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>🪙</span>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>New Face Template</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-mute, #94a3b8)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {/* Template Name Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8b949e)' }}>Template Name</label>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Gold Crest Heads Face"
                autoFocus
                style={{
                  backgroundColor: 'var(--input-bg, #0f172a)',
                  border: '1px solid var(--input-border, var(--border-color, #334155))',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: 'var(--input-text, var(--text-main, #ffffff))',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Face Side Selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8b949e)' }}>Coin Face Side</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setNewTemplateSide('heads')}
                  style={{
                    backgroundColor: newTemplateSide === 'heads' ? '#d97706' : 'var(--input-bg, #0f172a)',
                    color: newTemplateSide === 'heads' ? '#ffffff' : 'var(--text-main, #ffffff)',
                    border: `1.5px solid ${newTemplateSide === 'heads' ? '#fbbf24' : 'var(--border-color, #334155)'}`,
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>👑</span>
                  <span>HEADS (Front)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewTemplateSide('tails')}
                  style={{
                    backgroundColor: newTemplateSide === 'tails' ? '#2563eb' : 'var(--input-bg, #0f172a)',
                    color: newTemplateSide === 'tails' ? '#ffffff' : 'var(--text-main, #ffffff)',
                    border: `1.5px solid ${newTemplateSide === 'tails' ? '#60a5fa' : 'var(--border-color, #334155)'}`,
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>🦅</span>
                  <span>TAILS (Back)</span>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-main, #94a3b8)',
                  border: '1px solid var(--border-color, #334155)',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateNew}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: isDarkColor(resolveSolidColor(tenantConfig?.primary_color, '#38bdf8')) ? '#ffffff' : '#111827',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Create & Edit Face
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* SHARE FACE TEMPLATE ACCESS MODAL */}
      {showShareModal && sharingTemplate && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          backdropFilter: 'blur(4px)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            background: 'var(--card-bg, #1b1b1d)',
            border: '1px solid var(--border-color, #3c3c42)',
            borderRadius: '12px',
            width: '460px',
            maxWidth: '90vw',
            padding: '24px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            color: 'var(--text-main, #dfdfe3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #3c3c42)', paddingBottom: '12px' }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Share Face Template Access
                </span>
                <div style={{ fontSize: '11px', color: 'var(--text-mute, #8e8e93)', marginTop: '2px' }}>
                  {sharingTemplate.name}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '16px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', color: 'var(--text-main, #dfdfe3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={shareAllTenantUsers}
                  onChange={(e) => setShareAllTenantUsers(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--primary-color, #1677ff)' }}
                />
                <span>Share with all users in this tenant</span>
              </label>

              {!shareAllTenantUsers && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', borderTop: '1px solid var(--border-color, #2d2d31)', paddingTop: '12px' }}>
                  {/* Share with specific companies */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8e8e93)' }}>
                      Share with specific companies:
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <select
                        data-testid="share-company-dropdown"
                        value={selectedCompanyId}
                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg, #0c0c0e)',
                          border: '1px solid var(--input-border, var(--border-color, #3c3c42))',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          color: 'var(--input-text, var(--text-main, #dfdfe3))',
                          fontSize: '12px',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-- Select a Company to Share With --</option>
                        {availableCompanies
                          .filter(c => !sharedCompanies.some(sc => sc.id === c.id))
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        data-testid="add-share-company-btn"
                        disabled={!selectedCompanyId}
                        onClick={() => handleAddCompany()}
                        style={{
                          backgroundColor: selectedCompanyId ? 'var(--primary-color, #1677ff)' : 'var(--disabled-bg, #27272a)',
                          color: selectedCompanyId
                            ? (isDarkColor(resolveSolidColor(tenantConfig?.primary_color, '#1677ff')) ? '#ffffff' : '#111827')
                            : 'var(--text-mute, #71717a)',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: selectedCompanyId ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Add Company
                      </button>
                    </div>

                    {/* List of shared companies */}
                    <div style={{
                      minHeight: '40px',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      backgroundColor: 'var(--input-bg, #0c0c0e)',
                      border: '1px solid var(--border-color, #2d2d31)',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      {sharedCompanies.length === 0 ? (
                        <span style={{ fontSize: '11px', color: 'var(--text-mute, #55555c)', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>
                          No companies selected yet. Select a company above and click Add Company.
                        </span>
                      ) : (
                        sharedCompanies.map(comp => (
                          <div
                            key={comp.id}
                            data-testid={`shared-company-pill-${comp.id}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: 'rgba(37, 99, 235, 0.15)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}
                          >
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#93c5fd', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>🏢</span>
                              <span>{comp.name}</span>
                            </span>
                            <button
                              type="button"
                              data-testid={`remove-company-${comp.id}`}
                              onClick={() => handleRemoveCompany(comp.id)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#ff4d4f',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: '700'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Share with specific individual users (optional) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid var(--border-color, #222226)', paddingTop: '10px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8e8e93)' }}>Share with specific users (optional):</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        value={newShareEmail}
                        onChange={(e) => setNewShareEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newShareEmail.trim()) {
                            if (!sharedUserEmails.includes(newShareEmail.trim())) {
                              setSharedUserEmails([...sharedUserEmails, newShareEmail.trim()]);
                            }
                            setNewShareEmail('');
                          }
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg, #0c0c0e)',
                          border: '1px solid var(--input-border, var(--border-color, #3c3c42))',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          color: 'var(--input-text, var(--text-main, #dfdfe3))',
                          fontSize: '12px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newShareEmail.trim() && !sharedUserEmails.includes(newShareEmail.trim())) {
                            setSharedUserEmails([...sharedUserEmails, newShareEmail.trim()]);
                            setNewShareEmail('');
                          }
                        }}
                        style={{
                          backgroundColor: 'var(--primary-color, #1677ff)',
                          color: isDarkColor(resolveSolidColor(tenantConfig?.primary_color, '#1677ff')) ? '#ffffff' : '#111827',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Add
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '100px', overflowY: 'auto', marginTop: '4px' }}>
                      {sharedUserEmails.map(email => (
                        <span
                          key={email}
                          style={{
                            backgroundColor: 'var(--input-bg, #2d2d31)',
                            border: '1px solid var(--border-color, #3c3c42)',
                            color: 'var(--text-main, #dfdfe3)',
                            borderRadius: '12px',
                            padding: '3px 8px',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {email}
                          <span
                            onClick={() => setSharedUserEmails(sharedUserEmails.filter(e => e !== email))}
                            style={{ cursor: 'pointer', color: 'var(--text-mute, #8e8e93)', fontWeight: 'bold' }}
                          >
                            ✕
                          </span>
                        </span>
                      ))}
                      {sharedUserEmails.length === 0 && (
                        <span style={{ fontSize: '11px', color: 'var(--text-mute, #55555c)', fontStyle: 'italic' }}>
                          No individual users added.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #3c3c42)', paddingTop: '12px' }}>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border-color, #3c3c42)',
                  color: 'var(--text-main, #dfdfe3)',
                  borderRadius: '4px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="save-share-settings-btn"
                onClick={handleSaveShareSettings}
                style={{
                  backgroundColor: 'var(--primary-color, #1677ff)',
                  border: 'none',
                  color: isDarkColor(resolveSolidColor(tenantConfig?.primary_color, '#1677ff')) ? '#ffffff' : '#111827',
                  borderRadius: '4px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {renderImportModal()}
      {renderPushModal()}
    </div>
  );
}
